import React, { useMemo, useRef } from 'react';
import { observer } from 'mobx-react';
import { Field, Formik, FormikProps } from 'formik';
import * as FS from '../form.styles';
import { ClaimEditorStore, DISABLED_BILLING_PROVIDER_MESSAGE } from '../../../../stores/claim-editor';
import { SupportProvider } from '../../../../api';
import { AsyncSelectField } from '../../../base/form/async-select/async-select';
import { fieldDomId } from '../../../../stores/claim-editor/claim-editor-sections';
import { validateBillingProviderFormValues } from '../../../../stores/claim-editor/claim-editor-rules';
import { useClaimEditorRevealFieldErrors } from '../use-claim-editor-form-validation';

interface BillingProviderFormProps {
    claimEditorStore: ClaimEditorStore;
}

export const BillingProviderForm: React.FunctionComponent<BillingProviderFormProps> = observer(({ claimEditorStore }) => {
    const validationSignal = claimEditorStore.validationSignal;
    const validationEvaluated = claimEditorStore.validationEvaluated;
    const formikRef = useRef<FormikProps<{ providerLookup: string }> | null>(null);
    const initialValues = useMemo(() => ({
        providerLookup: claimEditorStore.billingProviderId || '',
    }), [claimEditorStore.billingProviderId]);

    return (
        <Formik
            innerRef={formikRef as any}
            initialValues={initialValues}
            validate={values =>
                validateBillingProviderFormValues(values, {
                    isProviderIdActive: claimEditorStore.isProviderIdActive,
                })
            }
            onSubmit={() => {}}
            enableReinitialize
            validateOnMount={false}
            validateOnChange={true}
            validateOnBlur={false}
        >
            {formik => {
                useClaimEditorRevealFieldErrors(formik, validationSignal, validationEvaluated);
                return (
                <>
                <FS.FormGrid>
                    <FS.FormField span={2} id={fieldDomId('billingProviderId')}>
                        <FS.Label required>Billing Provider Lookup</FS.Label>
                        <Field
                            name="providerLookup"
                            component={AsyncSelectField}
                            key={
                                claimEditorStore.areBillingProvidersLoaded
                                    ? 'billing-providers-ready'
                                    : 'billing-providers-loading'
                            }
                            placeholder="Type organization or first/last to search"
                            cacheOptions={claimEditorStore.areBillingProvidersLoaded}
                            loadOptions={async (q: string) => {
                                const query = (q || '').trim().toLowerCase();

                                if (!claimEditorStore.areBillingProvidersLoaded) {
                                    await claimEditorStore.preloadBillingProviders();
                                }

                                const list: SupportProvider[] = claimEditorStore.billingProviders || [];
                                const normalize = (val?: string) => (val ? val.toLowerCase() : '');
                                const startsWith = (val?: string) => (val ? val.startsWith(query) : false);

                                const filtered = !query
                                    ? list
                                    : list.filter((p: any) => {
                                          const first = normalize(p?.first || p?.provider?.first);
                                          const last = normalize(p?.last || p?.provider?.last);
                                          const org = normalize(
                                              (p as any)?.organization || (p as any)?.provider?.organization,
                                          );
                                          const npi = normalize((p as any)?.npi || (p as any)?.provider?.npi);
                                          return (
                                              startsWith(org) ||
                                              startsWith(first) ||
                                              startsWith(last) ||
                                              startsWith([first, last].filter(Boolean).join(' ')) ||
                                              startsWith([last, first].filter(Boolean).join(' ')) ||
                                              startsWith(npi)
                                          );
                                      });

                                return filtered.slice(0, 50).map(p => ({
                                    label: (p as any).organization || [p.first, p.last].filter(Boolean).join(' ') || '',
                                    value: p.id as any,
                                    provider: p,
                                    info: p.npi ? ' | ' + `NPI: ${p.npi}` : undefined,
                                } as any));
                            }}
                            getDefault={async (id: string) => {
                                if (!id) return null;
                                const provider = await claimEditorStore.getProviderById(id);
                                if (provider && claimEditorStore.isProviderIdActive(id)) {
                                    const label = (provider as any).organization || [provider.first, provider.last].filter(Boolean).join(' ') || '';
                                    return {
                                        label,
                                        value: provider.id as any,
                                        provider,
                                        info: provider.npi ? ' | ' + `NPI: ${provider.npi}` : undefined,
                                    } as any;
                                }
                                const label = provider
                                    ? ((provider as any).organization || [provider.first, provider.last].filter(Boolean).join(' ') || '')
                                    : '';
                                return {
                                    label: label ? `${label} (disabled)` : DISABLED_BILLING_PROVIDER_MESSAGE,
                                    value: id,
                                } as any;
                            }}
                            onChange={(opt: any, f: any) => {
                                const p: SupportProvider = opt?.provider;
                                if (!p) return;
                                claimEditorStore.updateBillingProviderId(String(p.id || ''));
                                f.setFieldValue('providerLookup', String(p.id || ''));
                            }}
                        />
                    </FS.FormField>
                </FS.FormGrid>
                </>
                );
            }}
        </Formik>
    );
});

export default BillingProviderForm;
